// The endpoint spec as the editor sees it, and the rules it mirrors from
// internal/platform/endpoints (parameter types per column, operators).
export type Spec = Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any

export interface Param {
  name: string
  in?: 'query' | 'path'
  field: string
  type: string
  ops?: string[]
  required?: boolean
  default?: unknown
  max_items?: number
  description?: string
}
export interface Fixed {
  field: string
  op?: string
  value: unknown
}
export interface Sort {
  field: string
  dir?: 'asc' | 'desc'
}
export interface Column {
  name: string
  type: string
  description?: string
}

// Mirrors internal/platform/endpoints: parameter type per column type and
// the operators each type supports.
export const paramType = (t: string) =>
  t === 'BIGINT' || t === 'INTEGER' ? 'integer' : t === 'DOUBLE' ? 'number' : t === 'BOOLEAN' ? 'boolean' : t === 'DATE' ? 'date' : 'string'
export const opsFor: Record<string, string[]> = {
  string: ['eq', 'ne', 'in', 'prefix', 'lt', 'lte', 'gt', 'gte'],
  integer: ['eq', 'ne', 'in', 'lt', 'lte', 'gt', 'gte'],
  number: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte'],
  boolean: ['eq', 'ne'],
  date: ['eq', 'ne', 'in', 'lt', 'lte', 'gt', 'gte'],
}
export const opLabel: Record<string, string> = { eq: '=', ne: '≠', in: 'in list', prefix: 'starts with', lt: '<', lte: '≤', gt: '>', gte: '≥' }

export const blankSpec = (): Spec => ({
  path: '/v1/',
  method: 'GET',
  bucket: '',
  summary: { sv: '', en: '' },
  fields: [],
  params: [],
  sort: [],
  pagination: { mode: 'cursor', default_limit: 100, max_limit: 1000 },
  tier: 'standard',
  auth: 'key_optional',
})

/** normalizeSpec marks parameters named in the path ({name}) as path parameters. */
export function normalizeSpec(spec: Spec): Spec {
  const path = String(spec.path ?? '')
  const params = ((spec.params ?? []) as Param[]).map((p) => {
    const { in: _, ...rest } = p // eslint-disable-line @typescript-eslint/no-unused-vars
    return path.includes(`{${p.name}}`) ? { ...rest, in: 'path' as const, ops: ['eq'] } : rest
  })
  return { ...spec, params }
}


/** Query string for the preview: required parameters with a placeholder, and a small limit. */
export function exampleQuery(spec: Spec): string {
  const q = new URLSearchParams()
  for (const p of (spec.params ?? []) as Param[]) {
    if (p.in !== 'path' && p.required && p.default === undefined) q.set(p.name, '')
  }
  if (!spec.single) q.set('limit', '10')
  return q.toString()
}
